import React from 'react'
import { AutoField, FieldLabel } from '@puckeditor/core'
import { useFontRegistry } from '../../fonts/FontRegistryContext'

const SYSTEM_OPTIONS = [
  { label: 'Default (inherit)', value: '' },
  { label: 'System Sans', value: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif' },
  { label: 'System Serif', value: 'Georgia, Cambria, "Times New Roman", Times, serif' },
  { label: 'Monospace', value: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' },
  { label: 'Site Header (var --font-header)', value: 'var(--font-header)' },
  { label: 'Site Copy (var --font-copy)', value: 'var(--font-copy)' },
  { label: 'Site Display (var --font-display)', value: 'var(--font-display)' },
]

const CUSTOM_VALUE = '__custom__'

export type FontFamilyFieldProps = {
  value?: string
  onChange: (value: string) => void
  id?: string
  label?: string
}

export const FontFamilyField: React.FC<FontFamilyFieldProps> = ({
  value = '',
  onChange,
  id,
  label = 'Font Family',
}) => {
  const { fonts } = useFontRegistry()

  const registeredOptions = fonts
    .filter(f => !!f.name)
    .map(f => ({ label: `Custom: ${f.name}`, value: `"${f.name}"` }))

  const knownValues = new Set([
    ...SYSTEM_OPTIONS.map(o => o.value),
    ...registeredOptions.map(o => o.value),
  ])

  const isCustom = !!value && !knownValues.has(value)
  const selectValue = isCustom ? CUSTOM_VALUE : value

  const selectOptions = [
    ...SYSTEM_OPTIONS,
    ...registeredOptions,
    { label: 'Custom…', value: CUSTOM_VALUE },
  ]

  return (
    <FieldLabel label={label}>
      <AutoField
        field={{ type: 'select', options: selectOptions }}
        value={selectValue}
        onChange={(next: string) => {
          if (next === CUSTOM_VALUE) {
            onChange(value && !knownValues.has(value) ? value : '')
          } else {
            onChange(next)
          }
        }}
        id={id}
      />
      {(selectValue === CUSTOM_VALUE || isCustom) && (
        <div style={{ marginTop: 4 }}>
          <AutoField
            field={{ type: 'text', placeholder: 'e.g. "My Font", sans-serif' } as any}
            value={value}
            onChange={(next: string) => onChange(next)}
            id={`${id}-custom`}
          />
        </div>
      )}
    </FieldLabel>
  )
}
